import type ConstructionAllDetailsGuiDto from "../../../dto/construction/ConstructionAllDetailsGuiDto";
import {useCallback, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";

import useConstructionNewHook from "../../../hooks/useConstructionNewHook";
import {useParams} from "react-router-dom";
import type {RootState} from "../../../store";
import {clear, load as loadConstruction} from "../constructionNewSlice";
import {load as loadConstructionList} from "../../../pages/constructionsNew/constructionsNewSlice";
import {getOrderByCreatedDate} from "../../../utils/DateFormatting";
import {useAuth} from "../../../services/auth/hooks/useAuth";
import {markAsRead} from "../../layout/messagesSlice";
import {MessageType} from "../../../services/signalR/models/Messages";

interface ConstructionNewPageHook {
  construction: ConstructionAllDetailsGuiDto | null;
  latestCalculationId: string | undefined;
  loading: boolean;
}

const useConstructionNewPageHook = (): ConstructionNewPageHook => {
  const dispatch = useDispatch();
  const {user} = useAuth();
  const {id} = useParams();
  const {all} = useSelector((state: RootState) => state.constructionsNew);
  const {data: construction, latestCalculationId} = useSelector((state: RootState) => state.constructionNew);
  const {messages} = useSelector((state: RootState) => state.signalRMessages);
  const {getConstructionById, getAllConstructions, getDefaultConstructionCalculationParameters} =
    useConstructionNewHook();
  const [loading, setLoading] = useState<boolean>(false);

  const allRef = useRef(all);
  useEffect(() => {
    allRef.current = all;
  }, [all]);

  const loadAsync = useCallback(async (): Promise<void> => {
    if (id !== undefined) {
      setLoading(true);
      const result = await getConstructionById(id);
      const defaultCalculationParameters = await getDefaultConstructionCalculationParameters();
      if (allRef.current.length <= 0) {
        const constructions = await getAllConstructions();
        dispatch(
          loadConstructionList({
            data: getOrderByCreatedDate(constructions?.data ?? []),
          }),
        );
      }
      dispatch(
        loadConstruction({
          currentConstruction: result?.data,
          defaultCalculationParams: defaultCalculationParameters?.data,
          latestCalculationId: undefined,
          all: allRef.current,
        }),
      );
      setLoading(false);
    }
  }, [
    dispatch,
    getAllConstructions,
    getConstructionById,
    getDefaultConstructionCalculationParameters,
    id,
  ]);

  useEffect(() => {
    void (async () => {
      await loadAsync();
    })();
    return () => {
      dispatch(clear());
    };
  }, [dispatch, id, loadAsync]);

  useEffect(() => {
    const eventsThatShouldTriggerDataRefresh = [MessageType.ConstructionUpdated, MessageType.ConstructionAccepted];
    const relatedMessages = messages.filter(
      m => m.data?.constructionId !== id && !m.isRead && m.user === user.userName,
    );
    void (async () => {
      if (relatedMessages?.length > 0) {
        const firstUnreadMessage = relatedMessages[0];
        dispatch(markAsRead(firstUnreadMessage.id));
        if (eventsThatShouldTriggerDataRefresh.some(e => e === firstUnreadMessage.type)) {
          await loadAsync();
        }
      }
    })();
  }, [dispatch, id, loadAsync, messages, user.userName]);

  return {
    construction,
    latestCalculationId,
    loading,
  };
};

export default useConstructionNewPageHook;
